import { useEffect, useState } from 'preact/hooks';
import { GlanceHeader } from '../components/GlanceHeader';
import { MediaImage } from '../components/MediaEl';
import { ApiError, listMedia, selectMedia, getResume, type ActorMedia, type Resume } from '../lib/api';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { mediaLabel, mbool } from './materialsPresentation';
import type { MaterialsSection } from '../routes/route';

export interface MaterialsMeProps {
  tenantId: string;
  userName: string;
  onNavigate: (section: MaterialsSection) => void;
}

// B5b — THIS IS ME: the identity glance. The name comes off the real session
// (same as Today's glance; no union/age is invented when no profile record
// backs it). Below it, the selectable "looks" — tapping a headshot toggles its
// is_selected flag (MULTI: several looks can represent you at once). Then two
// glance cards linking deeper: the auto-built resume (credit count) and the
// current reel.
export function MaterialsMe({ tenantId, userName, onNavigate }: MaterialsMeProps) {
  const [headshots, setHeadshots] = useState<ActorMedia[] | null>(null);
  const [reels, setReels] = useState<ActorMedia[] | null>(null);
  const [resume, setResume] = useState<Resume | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // A read that failed is a different fact from a read that found nothing —
  // each section says which one happened (S15's rule).
  const [headshotsFailure, setHeadshotsFailure] = useState<ReadFailure | null>(null);
  const [reelsFailure, setReelsFailure] = useState<ReadFailure | null>(null);
  const [resumeFailure, setResumeFailure] = useState<ReadFailure | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.allSettled([
      listMedia(tenantId, 'headshot'),
      listMedia(tenantId, 'reel'),
      getResume(tenantId),
    ]).then(([h, r, res]) => {
      if (cancelled) return;
      if (h.status === 'fulfilled') setHeadshots(h.value);
      else {
        setHeadshots([]);
        setHeadshotsFailure(describeReadFailure(h.reason));
      }
      if (r.status === 'fulfilled') setReels(r.value);
      else {
        setReels([]);
        setReelsFailure(describeReadFailure(r.reason));
      }
      if (res.status === 'fulfilled') setResume(res.value);
      else setResumeFailure(describeReadFailure(res.reason));
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  async function toggleSelected(m: ActorMedia) {
    if (busy) return;
    setBusy(m.id);
    setToggleError(null);
    const next = !mbool(m.fields, 'is_selected');
    try {
      await selectMedia(tenantId, m.id, { is_selected: next });
      setHeadshots((prev) =>
        (prev ?? []).map((x) => (x.id === m.id ? { ...x, fields: { ...x.fields, is_selected: next } } : x))
      );
    } catch (err) {
      setToggleError(
        `That look stayed as it was — ${err instanceof ApiError ? err.message : 'the server could not be reached'}`
      );
    } finally {
      setBusy(null);
    }
  }

  const creditCount = resume ? resume.creditGroups.reduce((n, g) => n + g.credits.length, 0) : null;
  const currentReel = (reels ?? []).find((r) => mbool(r.fields, 'is_current')) ?? (reels ?? [])[0] ?? null;

  return (
    <div class="materials__scroll">
      <GlanceHeader name={userName} />

      <div class="materials__eyebrow">Pick what represents you now</div>
      {headshots === null ? (
        <div class="materials__empty">One moment…</div>
      ) : headshotsFailure ? (
        <div class="materials__error">
          {headshotsFailure.headline} {headshotsFailure.detail}
        </div>
      ) : headshots.length === 0 ? (
        <button type="button" class="glancecard" onClick={() => onNavigate('headshots')}>
          <div class="glancecard__row">
            <span class="glancecard__name">Nothing set yet</span>
          </div>
          <div class="glancecard__sub">
            The headshots on your Headshots screen become your looks here.
          </div>
        </button>
      ) : (
        <div class="looks">
          {headshots.map((m) => {
            const on = mbool(m.fields, 'is_selected');
            return (
              <button
                key={m.id}
                type="button"
                class={`look${on ? ' look--on' : ''}`}
                aria-pressed={on}
                aria-label={`${mediaLabel(m)}${on ? ' — selected' : ''}`}
                disabled={busy === m.id}
                onClick={() => toggleSelected(m)}
              >
                <MediaImage tenantId={tenantId} id={m.id} alt={mediaLabel(m)} />
                {/* ⚑ THE WORD, NOT THE BORDER. Which looks are chosen was
                    said by an accent border and an aria-label and by nothing
                    a sighted person scanning the grid could read — a state
                    carried by a hue alone, which is the one thing this
                    product's rules forbid outright. The Headshots screen one
                    file over already says "selected" in words; this is the
                    two agreeing. */}
                {on ? <span class="look__now">selected</span> : null}
              </button>
            );
          })}
        </div>
      )}
      {toggleError ? <div class="materials__error">{toggleError}</div> : null}

      <button type="button" class="glancecard" onClick={() => onNavigate('resume')}>
        <div class="glancecard__row">
          <span class="glancecard__name">
            Resume{creditCount !== null ? ` · ${creditCount} credit${creditCount === 1 ? '' : 's'}` : ''}
          </span>
          <span class="glancecard__mini">auto</span>
        </div>
        <div class="glancecard__sub">
          {resumeFailure
            ? `couldn't be read right now — ${resumeFailure.headline.toLowerCase()}`
            : 'built from your bookings · tailored variants · a chosen headshot prints on it'}
        </div>
      </button>

      <button type="button" class="glancecard" onClick={() => onNavigate('reels')}>
        <div class="glancecard__row">
          <span class="glancecard__name">{currentReel ? mediaLabel(currentReel) : 'Demo reel'}</span>
          {currentReel && typeof currentReel.fields['duration'] === 'string' ? (
            <span class="glancecard__mini">{String(currentReel.fields['duration'])}</span>
          ) : null}
        </div>
        <div class="glancecard__sub">
          {reelsFailure
            ? `couldn't be read right now — ${reelsFailure.headline.toLowerCase()}`
            : currentReel
              ? 'current · older cuts in history'
              : 'no reel yet — the current cut shows here once one is in'}
        </div>
      </button>
    </div>
  );
}
